import { Fragment } from 'react';

import { format } from 'date-fns';

import { InlineDatePicker } from '@components/datepicker';
import { FilterListItem } from '@components/filter-list-item';
import { AnimatedRadio } from '@components/radio-group/animated-radio';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextField } from '@components/text-field';
import { LocaleCode } from '@locale/locale-store';
import { getScopedTranslate, useScopedTranslate } from '@locale/translate';
import { dateFnsLocales } from '@utils/date';

import type { MenuProps } from './types';

import { FilterActionButtons } from '../actions-buttons';
import { AppliedFilterData, FilterComparer } from '../common';
import { FilterSubheading } from '../subheading';

export const ABSOLUTE_COMPARERS: Array<FilterComparer> = ['after', 'on', 'before'];
export const RELATIVE_COMPARERS: Array<FilterComparer> = ['today', 'yesterday', 'gt', 'eq', 'lt'];
export const DATE_COMBINED_COMPARERS = [...ABSOLUTE_COMPARERS, RELATIVE_COMPARERS];

export const getDateDisplay = (filter: AppliedFilterData, lang: LocaleCode): string => {
  const { t } = getScopedTranslate(locale);

  if (filter.comparer === 'today' || filter.comparer === 'yesterday') {
    return '';
  } else if (ABSOLUTE_COMPARERS.includes(filter.comparer)) {
    if (filter.value) {
      // `lang` can be 'dk', which has no entry — format falls back to its
      // default (English) locale, same as the untyped lookup did before.
      return format(new Date(filter.value as string), 'do MMMM yyyy', {
        locale: dateFnsLocales[lang as keyof typeof dateFnsLocales],
      });
    }
  } else if (RELATIVE_COMPARERS.includes(filter.comparer)) {
    return `${filter.value} ${t('daysAgo')}`;
  }

  return '';
};

export const DateMenu = ({ state, actions, config }: MenuProps) => {
  const { t } = useScopedTranslate(locale);
  let disableApply = true;

  let relativeComparers = RELATIVE_COMPARERS;
  let absoluteComparers = ABSOLUTE_COMPARERS;

  if ('allowedAbsoluteComparers' in config && config.allowedAbsoluteComparers) {
    absoluteComparers = config.allowedAbsoluteComparers;
  }
  if ('allowedRelativeComparers' in config && config.allowedRelativeComparers) {
    relativeComparers = config.allowedRelativeComparers;
  }

  if (state.comparer) {
    if (Boolean(state.value)) {
      disableApply = false;
    } else if (['yesterday', 'today'].includes(state.comparer)) {
      disableApply = false;
    }
  }

  return (
    <Fragment>
      {state.comparer && absoluteComparers.includes(state.comparer) && (
        <InlineDatePicker
          value={state.value ? (state.value as string) : null}
          onChange={(val: Date) => actions.selectValue(val.toISOString())}
        />
      )}
      {!(state.comparer && absoluteComparers.includes(state.comparer)) && (
        <Fragment>
          <FilterSubheading text={t('relative')} />

          {relativeComparers.map((comparer) => {
            const isSelected = comparer === state.comparer;
            const isTodayOrYesterday = state.comparer === 'yesterday' || state.comparer === 'today';
            return (
              <Fragment key={comparer}>
                <FilterListItem
                  icon={<AnimatedRadio checked={state.comparer === comparer} />}
                  label={t(`core.comparator.${comparer}`).replace('...', '')}
                  onClick={() => actions.selectComparer(comparer)}
                />
                {isSelected && !isTodayOrYesterday && (
                  <Stack space={2} orientation="horizontal" align="center">
                    <TextField
                      autoFocus
                      hideLabel
                      label={t(`core.comparator.${comparer}`).replace('...', '')}
                      value={state.value as string}
                      onChange={(val) => {
                        if (!val || /^\d+$/.test(val)) {
                          actions.selectValue(val);
                        }
                      }}
                    />
                    <Text font="regular14" color="textSecondary" text={t('daysAgo')} />
                  </Stack>
                )}
              </Fragment>
            );
          })}

          <FilterSubheading text={t('absolute')} />

          {absoluteComparers.map((comparer) => (
            <FilterListItem
              key={comparer}
              label={t(`core.comparator.${comparer}`).replace('...', '')}
              onClick={() => {
                actions.selectComparer(comparer);
                actions.selectValue(new Date().toISOString());
              }}
            />
          ))}
        </Fragment>
      )}

      <FilterActionButtons state={state} actions={actions} disableApply={disableApply} />
    </Fragment>
  );
};

const locale = {
  en: {
    relative: 'Relative',
    absolute: 'Absolute',
    daysAgo: 'days ago',
  },
  is: {
    relative: 'Miða við',
    absolute: 'Dagsetning',
    daysAgo: 'dögum síðan',
  },
};
